package one.healthium.meal_plans.data

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import loco.CONTENT_MEAL_PLAN_PERSONAL_LABEL
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserRepository
import one.healthium.loco.asLString
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.meal_plans.domain.MealPlanRepository
import one.primehealth.app.BaseAppConfig

internal class AppMealPlanRepository(
    private val apiClient: MealPlansApiClient,
    private val mealPlanMapper: MealPlanMapper,
    private val appPreferences: AppPreferences,
    private val userRepository: UserRepository,
    private val appConfig: BaseAppConfig,
) : MealPlanRepository {

    private var mealPlanInfosCache: List<MealPlanInfoModel>? = null
    private val mealPlanCacheMutex = Mutex()

    override fun getMealPlanInfos(): Flow<List<MealPlanInfoModel>> {
        return flow {
            val mealPlanInfos = mealPlanCacheMutex.withLock {
                val cachedValue = mealPlanInfosCache

                if (cachedValue != null) {
                    cachedValue
                } else {
                    val mealPlanInfosDto = apiClient.getMealPlanInfos()
                    val mealPlanInfosModel = buildList {
                        if (appConfig.features.personalMealPlanEnabled) {
                            add(createPersonalMealPlan())
                        }
                        addAll(mealPlanInfosDto.map(mealPlanMapper::dtoToModel))
                    }
                    mealPlanInfosCache = mealPlanInfosModel
                    mealPlanInfosModel
                }
            }

            emit(mealPlanInfos)
        }
    }

    override fun getCurrentMealPlan(): Flow<MealPlanInfoModel?> {
        return combine(
            getMealPlanInfos(),
            appPreferences.mealPlanIdFlow
        ) { mealPlanInfos, mealPlanId ->
            val validatedMealPlanId = if (mealPlanId == MealPlanInfoModel.PERSONAL_ID && !appConfig.features.personalMealPlanEnabled) {
                null
            } else {
                mealPlanId
            }
            mealPlanInfos.firstOrNull { it.id == validatedMealPlanId } ?: tryApplyDefaultMealPlan(mealPlanInfos)
        }
    }

    override suspend fun setCurrentMealPlan(id: String) {
        appPreferences.mealPlanId = id
    }

    private suspend fun tryApplyDefaultMealPlan(mealPlanInfos: List<MealPlanInfoModel>): MealPlanInfoModel? {
        val defaultMealPlan = getDefaultMealPlan(mealPlanInfos) ?: return null
        appPreferences.mealPlanId = defaultMealPlan.id
        return defaultMealPlan
    }

    private suspend fun getDefaultMealPlan(mealPlanInfos: List<MealPlanInfoModel>): MealPlanInfoModel? {
        if (appConfig.features.personalMealPlanEnabled) {
            mealPlanInfos
                .firstOrNull { it.id == MealPlanInfoModel.PERSONAL_ID }
                ?.let { return it }
        }

        val user = userRepository.getUser().firstOrNull() ?: return mealPlanInfos.firstOrNull()
        val meatPrefer = user.meatPrefer?.map { it.key }?.toSet() ?: return mealPlanInfos.firstOrNull()

        return mealPlanInfos.maxByOrNull { mealPlanInfo ->
            mealPlanInfo.tags.intersect(meatPrefer).size
        }
            ?: mealPlanInfos.firstOrNull()
    }

    private fun createPersonalMealPlan(): MealPlanInfoModel {
        return MealPlanInfoModel(
            id = MealPlanInfoModel.PERSONAL_ID,
            name = loco.Res.string.CONTENT_MEAL_PLAN_PERSONAL_LABEL.asLString(),
            tags = emptyList()
        )
    }
}